// src/api/auth.js
import api from '../services/api'; // Import the configured API client
import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  isAuthenticated
} from '../utils/authToken';

// Use the API URL from environment variables
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const authApi = {
  async requestOTP(email) {
    try {
      const response = await api.post('/api/auth/login', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async verifyOTP(email, otp) {
    try {
      const response = await api.post('/api/auth/verify', { email, otp });

      if (!response.data.token) {
        throw new Error('No token received in response');
      }

      return response.data.token;  // Don't set the token here, let the context handle it
    } catch (error) {
      throw error;
    }
  },

  async logout() {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }

      await api.post('/api/auth/logout', {});

      removeAuthToken();
      return true;
    } catch (error) {
      // Still remove the token even if the server request fails
      removeAuthToken();
      throw error;
    }
  },

  async checkSession() {
    try {
      const token = getAuthToken();
      if (!token) {
        return false;
      }

      // First try using the auth/check-session endpoint
      try {
        const response = await api.get('/api/auth/check-session');
        return response.data.valid;
      } catch (endpointError) {
        // Continue to fallback validation
      }

      // Fallback: If the check-session endpoint fails or returns an error,
      // we can do a simple token validation by checking if it has expired
      try {
        // JWT tokens are in the format: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
          return false;
        }

        // The payload (second part) is Base64Url encoded
        // Need to handle base64url format by replacing - with + and _ with /
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        const payload = JSON.parse(jsonPayload);

        // Check if token has an expiration claim
        if (payload.exp) {
          // exp is in seconds, Date.now() is in milliseconds
          return payload.exp * 1000 > Date.now();
        }

        // If no expiration specified, assume it's valid
        return true;
      } catch (tokenError) {
        // Couldn't validate the token - assume it's invalid
        return false;
      }
    } catch (error) {
      return false;
    }
  },

  // Use the utility functions
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  isAuthenticated
};

export default authApi;